/**
 * MailboxSettings Component
 *
 * Email mailbox configuration tab in System Settings.
 * Allows creating a mailbox address (prefix + auto-domain),
 * toggling active/inactive, and deleting.
 */
import { useState, useEffect } from 'react';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { Badge, Button, Divider, Icon, Paper, SectionLabel } from '@insio/ui-toolkit';
import { useMailAddressSettings } from '../../../hooks/use-mail-address-settings';
import { MailboxCreateForm } from './MailboxCreateForm';
import { MailSenderAllowlist } from './MailSenderAllowlist';
import { QuarantinedEmails } from './QuarantinedEmails';

export function MailboxSettings() {
  const {
    t,
    mailAddress,
    hasMailbox,
    loading,
    error,
    prefix,
    domain,
    previewAddress,
    validationErrorMessage,
    serverError,
    handlePrefixChange,
    handleCreate,
    handleToggleEnabled,
    handleDelete,
    handleCopyAddress,
    creating,
    isBusy
  } = useMailAddressSettings();

  const showLoading = useDelayedLoading(loading);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!hasMailbox) setShowDeleteConfirm(false);
  }, [hasMailbox]);

  if (!hasMailbox && loading) {
    return showLoading ? (
      <div className='text-sm text-foreground-muted py-lg'>{t('loading')}</div>
    ) : null;
  }

  if (error) {
    return <div className='text-sm text-error-text py-lg'>{t('error')}</div>;
  }

  const displayError = validationErrorMessage ?? serverError;

  return (
    <div className='space-y-lg'>
      {hasMailbox ? (
        /* ── Existing mailbox ────────────────────────────── */
        <Paper showDivider={false}>
          {/* Removing p-lg from the paper will affect all implementations, so its better to leave this as a special case with a margin */}
          <Paper.Content className='space-y-lg -m-lg'>
            <SectionLabel>{t('sections.currentMailbox')}</SectionLabel>

            {/* Email address + status card */}
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md rounded-lg border border-border-base px-lg py-md'>
              <div className='flex items-center gap-md'>
                <div className='flex h-10 w-10 items-center justify-center rounded-full bg-primary-bg'>
                  <Icon name='envelope' size='md' className='text-primary-base' />
                </div>
                <div>
                  <div className='flex items-center gap-sm'>
                    <span className='text-base font-semibold text-foreground'>
                      {mailAddress?.fullAddress}
                    </span>
                    <button
                      type='button'
                      onClick={() => void handleCopyAddress()}
                      className='text-foreground-muted hover:text-foreground transition-colors'
                      title={t('actions.copy')}
                    >
                      <Icon name='copy' size='sm' />
                    </button>
                  </div>
                  <span className='text-xs text-foreground-muted'>{t('fields.entityType')}</span>
                </div>
              </div>
              {mailAddress?.enabled ? (
                <Badge color='success' size='sm'>
                  {t('status.active')}
                </Badge>
              ) : (
                <Badge color='error' size='sm'>
                  {t('status.inactive')}
                </Badge>
              )}
            </div>

            {/* Info hint — how to use */}
            <div className='flex gap-sm rounded-lg bg-info-bg px-md py-sm'>
              <Icon name='circle-info' size='sm' className='text-info-text mt-2xs shrink-0' />
              <p className='text-sm text-info-text'>{t('hints.usage')}</p>
            </div>

            <Divider orientation='horizontal' size='full' variant='light' thickness='sm' />

            {/* Actions */}
            <div className='flex justify-end gap-sm'>
              {!showDeleteConfirm ? (
                <>
                  <Button
                    variant={mailAddress?.enabled ? 'error' : 'success'}
                    onClick={() => void handleToggleEnabled()}
                    disabled={isBusy}
                    testId='mailbox-toggle-enabled'
                  >
                    {mailAddress?.enabled ? t('actions.deactivate') : t('actions.activate')}
                  </Button>
                  <Button
                    variant='ghost'
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isBusy}
                    testId='mailbox-delete-trigger'
                  >
                    {t('actions.delete')}
                  </Button>
                </>
              ) : (
                <div className='flex items-center gap-sm'>
                  <span className='text-sm text-error-text'>{t('deleteConfirm.message')}</span>
                  <Button
                    variant='error'
                    onClick={() => {
                      void handleDelete();
                      setShowDeleteConfirm(false);
                    }}
                    disabled={isBusy}
                    testId='mailbox-delete-confirm'
                  >
                    {t('deleteConfirm.confirm')}
                  </Button>
                  <Button
                    variant='ghost'
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isBusy}
                    testId='mailbox-delete-cancel'
                  >
                    {t('deleteConfirm.cancel')}
                  </Button>
                </div>
              )}
            </div>
          </Paper.Content>
        </Paper>
      ) : (
        /* ── Create mailbox ──────────────────────────────── */
        <Paper showDivider={false}>
          {/* Removing p-lg from the paper will affect all implementations, so its better to leave this as a special case with a margin */}
          <Paper.Content className='space-y-md -m-lg'>
            <SectionLabel>{t('sections.createMailbox')}</SectionLabel>

            <MailboxCreateForm
              t={t}
              prefix={prefix}
              domain={domain}
              previewAddress={previewAddress}
              displayError={displayError}
              handlePrefixChange={handlePrefixChange}
              handleCreate={handleCreate}
              creating={creating}
              isBusy={isBusy}
            />
          </Paper.Content>
        </Paper>
      )}

      {/* ── Sender policy ───────────────────────────────── */}
      {hasMailbox && (
        <>
          <QuarantinedEmails />
          <MailSenderAllowlist />
        </>
      )}
    </div>
  );
}
